import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { SkuSupplyCandidate, SkuSupplyCandidateIncompatibleReason } from '@nadoutong/contracts';
import { capabilityStatusLabel } from '../../pages/capabilityStatus.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Td, Tr } from '../../ui/index.ts';
import { SUPPLY_ID_CELL_CLASS } from './SupplyTableHead.tsx';

/**
 * One bindable candidate (#1327 PR-4, re-homed into the merged panel by
 * #1428).
 *
 * There is deliberately no cost input here. Procurement multipliers are owned
 * by the upstream page at (channel × upstream model) grain (#1329) and the
 * bind gate rejects an unpriced target outright, so an inline field would
 * create a second writer for a value this page does not own. An unpriced
 * candidate renders disabled with a link to the page that does own it.
 */

function incompatibleReasonKey(reason: SkuSupplyCandidateIncompatibleReason): I18nKey {
  return reason === 'protocol_mismatch'
    ? 'sku_detail.supply.reason.protocol_mismatch'
    : 'sku_detail.supply.reason.egress_incompatible';
}

export function CandidateSupplyRow({
  candidate,
  onBind,
  binding,
}: {
  readonly candidate: SkuSupplyCandidate;
  readonly onBind: (candidate: SkuSupplyCandidate) => void;
  readonly binding: boolean;
}): JSX.Element {
  const t = useT();
  const unpriced = candidate.cost_multiplier === null;
  const blocked = !candidate.compatible || unpriced;
  const identityLine =
    `${candidate.upstream_id} · ${candidate.account_id} · ${candidate.key_bucket_id}`;

  return (
    <Tr data-testid={`sku-supply-row-${candidate.capability_id}`}>
      <Td>
        <div className={SUPPLY_ID_CELL_CLASS}>
          <span className="block truncate text-paper" title={candidate.upstream_name}>
            {candidate.upstream_name}
          </span>
          <span
            className="mt-0.5 block truncate font-mono text-micro text-paper-low"
            title={identityLine}
          >
            {identityLine}
          </span>
        </div>
      </Td>
      <Td mono>
        <div className={SUPPLY_ID_CELL_CLASS}>
          <span className="block truncate text-paper" title={candidate.upstream_channel_slug}>
            {candidate.upstream_channel_slug}
          </span>
          <span
            className="mt-0.5 block truncate text-micro text-paper-low"
            title={candidate.upstream_channel_id}
          >
            {candidate.upstream_channel_id}
          </span>
        </div>
      </Td>
      <Td mono>
        {/* Capped for the same reason as the two columns above — see #1442. */}
        <div className={SUPPLY_ID_CELL_CLASS}>
          <span className="block truncate text-paper" title={candidate.model}>
            {candidate.model}
          </span>
          <span
            className="mt-0.5 block truncate text-micro text-paper-low"
            title={candidate.capability_id}
          >
            {candidate.capability_id}
          </span>
        </div>
      </Td>
      <Td>{capabilityStatusLabel(candidate.probe_status, t)}</Td>
      <Td num>
        <span data-testid={`sku-supply-multiplier-${candidate.capability_id}`}>
          {candidate.cost_multiplier === null
            ? t('sku_detail.supply.multiplier_missing')
            : String(candidate.cost_multiplier)}
        </span>
      </Td>
      {/* Scheduling is a property of a bound row; a candidate has none yet. */}
      <Td className="text-right text-paper-low">—</Td>
      <Td className="text-right">
        <div className="flex flex-col items-end gap-1.5">
          <Button
            size="sm"
            disabled={blocked || binding}
            onClick={() => onBind(candidate)}
            data-testid={`sku-supply-bind-${candidate.capability_id}`}
          >
            {t('sku_detail.supply.bind')}
          </Button>
          {candidate.incompatible_reason !== null ? (
            <span className="text-right text-micro text-warn">
              {t(incompatibleReasonKey(candidate.incompatible_reason))}
            </span>
          ) : null}
          {unpriced && candidate.compatible ? (
            <span className="flex flex-wrap items-center justify-end gap-1 text-right text-micro text-warn">
              {t('sku_detail.supply.unpriced_hint')}
              <Link
                to={`/supply/upstreams/${encodeURIComponent(candidate.upstream_id)}`}
                className="inline-flex items-center gap-1 text-signal transition-colors duration-120 hover:text-signal-bright"
                data-testid={`sku-supply-upstream-link-${candidate.capability_id}`}
              >
                {t('sku_detail.supply.open_upstream')}
                <ExternalLink aria-hidden="true" className="h-3 w-3" />
              </Link>
            </span>
          ) : null}
        </div>
      </Td>
    </Tr>
  );
}
